import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Header } from '../components/layout/Header';
import { useAuth } from '../contexts/AuthContext';
import { api } from '../services/api';
import type { ExpenseCategory } from '../types';

function formatMoney(amount: number): string {
  return new Intl.NumberFormat('es-MX', {
    style: 'currency',
    currency: 'MXN',
  }).format(amount);
}

export function SettingsPage() {
  const { user, logout } = useAuth();
  const queryClient = useQueryClient();
  const [newCategory, setNewCategory] = useState({ name: '', icon: '', monthly_budget: 0 });
  const [showCategoryForm, setShowCategoryForm] = useState(false);

  const { data: categories } = useQuery({
    queryKey: ['expenseCategories'],
    queryFn: () => api.getExpenseCategories(),
  });

  const createCategoryMutation = useMutation({
    mutationFn: (data: { name: string; icon: string; monthly_budget: number }) =>
      api.createExpenseCategory(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['expenseCategories'] });
      setNewCategory({ name: '', icon: '', monthly_budget: 0 });
      setShowCategoryForm(false);
    },
  });

  const handleCategorySubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (newCategory.name) {
      createCategoryMutation.mutate(newCategory);
    }
  };

  const commonIcons = ['🍔', '🚗', '🏠', '💊', '📚', '🎮', '👕', '🎬', '✈️', '💡', '📱', '🛒'];

  return (
    <div>
      <Header title="Configuración" />

      {/* User Profile */}
      <div className="card mb-6">
        <h2 className="text-lg font-semibold text-primary-500 mb-4">👤 Mi Perfil</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="p-4 bg-dark-bg rounded-lg">
            <div className="text-sm text-gray-500 mb-1">Nombre</div>
            <div className="font-medium">
              {user?.first_name} {user?.last_name}
            </div>
          </div>
          <div className="p-4 bg-dark-bg rounded-lg">
            <div className="text-sm text-gray-500 mb-1">Email</div>
            <div className="font-medium">{user?.email}</div>
          </div>
          <div className="p-4 bg-dark-bg rounded-lg">
            <div className="text-sm text-gray-500 mb-1">Teléfono</div>
            <div className="font-medium">{user?.phone || '—'}</div>
          </div>
          <div className="p-4 bg-dark-bg rounded-lg">
            <div className="text-sm text-gray-500 mb-1">Negocio</div>
            <div className="font-medium">{user?.tenant?.name || '—'}</div>
          </div>
        </div>
        <div className="mt-4">
          <button onClick={logout} className="btn btn-secondary">
            🚪 Cerrar Sesión
          </button>
        </div>
      </div>

      {/* Expense Categories */}
      <div className="card mb-6">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-lg font-semibold text-primary-500">📁 Categorías de Gastos</h2>
          {!showCategoryForm && (
            <button
              onClick={() => setShowCategoryForm(true)}
              className="btn btn-primary text-sm"
            >
              ➕ Nueva
            </button>
          )}
        </div>

        {showCategoryForm && (
          <form onSubmit={handleCategorySubmit} className="mb-4 p-4 bg-dark-bg rounded-lg">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
              <div>
                <label className="label">Nombre</label>
                <input
                  type="text"
                  className="input"
                  placeholder="Ej: Comida"
                  value={newCategory.name}
                  onChange={(e) => setNewCategory({ ...newCategory, name: e.target.value })}
                  required
                />
              </div>
              <div>
                <label className="label">Icono</label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    className="input w-16 text-center"
                    maxLength={2}
                    value={newCategory.icon}
                    onChange={(e) => setNewCategory({ ...newCategory, icon: e.target.value })}
                    placeholder="🍔"
                  />
                  <div className="flex flex-wrap gap-1">
                    {commonIcons.slice(0, 6).map((icon) => (
                      <button
                        key={icon}
                        type="button"
                        className="p-1 hover:bg-dark-border rounded"
                        onClick={() => setNewCategory({ ...newCategory, icon })}
                      >
                        {icon}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
              <div>
                <label className="label">Presupuesto mensual</label>
                <input
                  type="number"
                  className="input"
                  step="100"
                  value={newCategory.monthly_budget || ''}
                  onChange={(e) =>
                    setNewCategory({ ...newCategory, monthly_budget: parseFloat(e.target.value) || 0 })
                  }
                  placeholder="0 = sin límite"
                />
              </div>
            </div>
            <div className="flex gap-3">
              <button
                type="submit"
                className="btn btn-primary text-sm"
                disabled={createCategoryMutation.isPending}
              >
                {createCategoryMutation.isPending ? 'Guardando...' : 'Guardar'}
              </button>
              <button
                type="button"
                className="btn btn-secondary text-sm"
                onClick={() => setShowCategoryForm(false)}
              >
                Cancelar
              </button>
            </div>
          </form>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {categories?.map((cat: ExpenseCategory) => (
            <div key={cat.id} className="flex items-center gap-3 p-3 bg-dark-bg rounded-lg">
              <span className="text-2xl">{cat.icon || '📁'}</span>
              <div className="flex-1">
                <div className="font-medium">{cat.name}</div>
                {cat.monthly_budget ? (
                  <div className="text-sm text-gray-500">
                    Presupuesto: {formatMoney(cat.monthly_budget)}
                  </div>
                ) : (
                  <div className="text-sm text-gray-600">Sin presupuesto</div>
                )}
              </div>
            </div>
          ))}
          {!categories?.length && (
            <p className="text-gray-500 col-span-full text-center py-4">
              No hay categorías configuradas
            </p>
          )}
        </div>
      </div>

      {/* App Info */}
      <div className="card">
        <h2 className="text-lg font-semibold text-primary-500 mb-4">ℹ️ Información</h2>
        <div className="space-y-2 text-sm text-gray-400">
          <div className="flex justify-between">
            <span>Versión</span>
            <span>1.0.0</span>
          </div>
          <div className="flex justify-between">
            <span>API</span>
            <span>{import.meta.env.VITE_API_URL || 'http://localhost:8080'}</span>
          </div>
          <div className="flex justify-between">
            <span>Tenant ID</span>
            <span className="font-mono text-xs">{user?.tenant_id || '—'}</span>
          </div>
        </div>
      </div>
    </div>
  );
}
